import {
  FiCalendar,
  FiMail,
  FiMapPin,
  FiUsers,
} from "react-icons/fi";
import EmployeeActions from "./EmployeeActions";
import StatusBadge from "./StatusBadge";

export default function EmployeeTable({ employees, onDeleteEmployee, onActivateEmployee }) {
  const showCtc = employees.some(employee => employee.ctc != null);
  const ctcAmount = value => value == null ? "—" : Number(value).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });

  const officialName = (employee) =>
    employee.name || employee.fullName || "Employee";

  const displayName = (employee) =>
    employee.nickname?.trim() || officialName(employee);

  const getInitials = (name) =>
    String(name || "Employee")
      .split(" ")
      .filter(Boolean)
      .map((word) => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const formatDate = (value) => {
    if (!value) return "—";

    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <>
      <div className="hidden overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm transition-shadow duration-300 hover:shadow-md lg:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-gray-100 bg-gray-50">
              <tr>
                <TableHead>Employee</TableHead>
                <TableHead>Employee ID</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Designation</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Status</TableHead>
                {showCtc && <TableHead>Annual CTC</TableHead>}
                <TableHead align="right">Actions</TableHead>
              </tr>
            </thead>

            <tbody>
              {employees.length ? (
                employees.map((employee) => (
                  <tr
                    key={employee.employeeId}
                    className="group border-b border-gray-100 transition-colors last:border-b-0 hover:bg-red-50"
                  >
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-red-700 to-red-900 font-semibold text-white shadow-md transition-transform group-hover:scale-105">
                          {getInitials(displayName(employee))}
                        </div>

                        <div>
                          <p className="font-medium text-gray-900">
                            {displayName(employee)}
                          </p>

                          {employee.nickname ? (
                            <p className="mt-0.5 text-xs text-gray-500">
                              Official name: {officialName(employee)}
                            </p>
                          ) : null}

                          <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-500">
                            <FiMail size={12} className="text-gray-400" />
                            {employee.email || "No email address"}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-5">
                      <span className="rounded-lg bg-gray-50 px-2.5 py-1 font-mono text-xs font-semibold text-gray-500">
                        {employee.id}
                      </span>
                    </td>

                    <td className="px-6 py-5">
                      <span className="inline-flex rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700">
                        {employee.department || "—"}
                      </span>
                    </td>

                    <td className="px-6 py-5 text-sm text-gray-700">
                      {employee.designation || "—"}
                    </td>

                    <td className="px-6 py-5 text-sm text-gray-700">
                      {employee.branch || "—"}
                    </td>

                    <td className="px-6 py-5">
                      <StatusBadge status={employee.status} />
                    </td>
                    {showCtc && <td className="px-6 py-5 text-sm text-gray-700 whitespace-nowrap">{ctcAmount(employee.ctc)}</td>}

                    <td className="px-6 py-5">
                      <div className="flex justify-end"><EmployeeActions employee={employee} onDeleteEmployee={onDeleteEmployee} onActivateEmployee={onActivateEmployee} /></div>
                    </td>
                  </tr>
                ))
              ) : (
                <EmptyRow />
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-4 lg:hidden">
        {employees.length ? (
          employees.map((employee) => (
            <div
              key={employee.employeeId}
              className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-red-700 to-red-900 font-semibold text-white shadow-md">
                  {getInitials(displayName(employee))}
                </div>

                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900">
                    {displayName(employee)}
                  </h3>

                  {employee.nickname ? (
                    <p className="mt-0.5 text-xs text-gray-500">
                      Official name: {officialName(employee)}
                    </p>
                  ) : null}

                  <p className="mt-1 text-sm text-gray-500">
                    {employee.designation || "No designation"}
                  </p>

                  <span className="mt-1.5 inline-flex rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700">
                    {employee.department || "No department"}
                  </span>
                </div>

                <StatusBadge status={employee.status} />
              </div>

              <div className="mt-5 space-y-3 border-t border-gray-100 pt-4 text-sm">
                {employee.ctc != null && <div className="flex items-center justify-between"><span className="text-gray-500">Annual CTC</span><span className="font-medium text-gray-900">{ctcAmount(employee.ctc)}</span></div>}
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Employee ID</span>

                  <span className="rounded-lg bg-gray-50 px-2.5 py-1 font-mono text-xs font-semibold text-gray-500">
                    {employee.id}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-gray-500">
                    <FiMapPin size={14} />
                    Branch
                  </span>

                  <span className="font-medium text-gray-900">
                    {employee.branch || "—"}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-gray-500">
                    <FiCalendar size={14} />
                    Joining Date
                  </span>

                  <span className="font-medium text-gray-900">
                    {formatDate(employee.joiningDate)}
                  </span>
                </div>
              </div>

              <div className="flex justify-end"><EmployeeActions employee={employee} onDeleteEmployee={onDeleteEmployee} onActivateEmployee={onActivateEmployee} /></div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center gap-3 rounded-3xl border border-gray-100 bg-white py-16 shadow-sm">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-50 text-gray-400">
              <FiUsers size={22} />
            </div>

            <p className="text-gray-500">No employees found.</p>
          </div>
        )}
      </div>
    </>
  );
}

function TableHead({ children, align = "left" }) {
  return (
    <th
      className={`px-6 py-4 text-${align} text-xs font-semibold uppercase tracking-wider text-gray-500`}
    >
      {children}
    </th>
  );
}

function EmptyRow() {
  return (
    <tr>
      <td colSpan={7} className="px-6 py-16 text-center">
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-50 text-gray-400">
            <FiUsers size={22} />
          </div>

          <p className="text-gray-500">No employees found.</p>
        </div>
      </td>
    </tr>
  );
}
